import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, Minus, Package, XCircle } from "lucide-react";
import { getOrdersReport } from "@/api/reports";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { RatingStars } from "@/features/reports/components/RatingStars";
import { cn } from "@/lib/cn";
import type { OrderReportRow, ReportParams } from "@/types/reports";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";

interface Props {
  params: ReportParams;
  visibleKeys: string[];
}

const PAGE_SIZE = 20;
const SKELETON_ROWS = 5;

const STATUS_STYLE: Record<
  string,
  { bg: string; text: string; dot: string; i18nKey: string }
> = {
  new: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    dot: "bg-blue-400",
    i18nKey: "orders.status.new",
  },
  in_route: {
    bg: "bg-orange-50",
    text: "text-orange-700",
    dot: "bg-orange-400",
    i18nKey: "orders.status.in_route",
  },
  pending: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-400",
    i18nKey: "orders.status.pending",
  },
  visited: {
    bg: "bg-sky-50",
    text: "text-sky-700",
    dot: "bg-sky-400",
    i18nKey: "orders.status.visited",
  },
  confirmed: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
    i18nKey: "orders.status.confirmed",
  },
  rejected: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    dot: "bg-rose-500",
    i18nKey: "orders.status.rejected",
  },
  skipped: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-400",
    i18nKey: "orders.status.skipped",
  },
  archived: {
    bg: "bg-gray-100",
    text: "text-gray-600",
    dot: "bg-gray-400",
    i18nKey: "orders.status.archived",
  },
};

function formatTimeWindow(start: string | null, end: string | null): string {
  if (!start || !end) return "—";
  return `${start.slice(0, 5)}–${end.slice(0, 5)}`;
}

/**
 * The actual arrival instant, in the TENANT's timezone (TASK-5043).
 *
 * `actual_arrival_at` is a UTC instant, and this column sits beside the
 * delivery TIME WINDOW — which is a tenant wall clock. Rendering the arrival in
 * the dispatcher's browser zone put the two in different frames, so «arrived
 * 12:05» could look late against a «13:00–14:00» window it actually met. That
 * is not a cosmetic offset: the on-time column next to it is computed by the
 * backend in the tenant's zone, so the table could show a ✓ beside a time that
 * read as outside the window.
 *
 * `locale` keeps its own `ru-RU`/`en-GB` pinning (pre-existing): it forces a
 * 24-hour clock regardless of the UI language's region.
 */
function formatArrival(
  dt: string | null,
  locale: string,
  tenantTz: string,
): string {
  if (!dt) return "—";
  return new Date(dt).toLocaleString(locale === "ru" ? "ru-RU" : "en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: tenantTz,
  });
}

function OnTimeIcon({ value }: { value: boolean | null }) {
  if (value === true)
    return <CheckCircle className="h-4 w-4 text-emerald-500" />;
  if (value === false) return <XCircle className="h-4 w-4 text-rose-500" />;
  return <Minus className="h-4 w-4 text-gray-400" />;
}

function renderOrderCell(
  key: string,
  row: OrderReportRow,
  t: (k: string) => string,
  locale: string,
  tenantTz: string,
): React.ReactNode {
  const st = STATUS_STYLE[row.status] ?? STATUS_STYLE.new;
  switch (key) {
    case "orderName":
      return (
        <span className="font-semibold text-gray-900">{row.order_name}</span>
      );
    case "customer":
      return row.customer_name ? (
        <span className="text-gray-500">{row.customer_name}</span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    case "address":
      return (
        <span className="max-w-[180px] truncate text-gray-500">
          {row.address}
        </span>
      );
    case "route":
      return <span className="text-gray-500">{row.route_name}</span>;
    case "vehicle":
      return row.vehicle_number != null ? (
        <span className="text-gray-500">{row.vehicle_number}</span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    case "status":
      return (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold",
            st.bg,
            st.text,
          )}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", st.dot)} />
          {t(st.i18nKey)}
        </span>
      );
    case "timeWindow":
      return (
        <span className="text-gray-500">
          {formatTimeWindow(row.time_window_start, row.time_window_end)}
        </span>
      );
    case "arrivedAt":
      return (
        <span className="text-gray-500">
          {formatArrival(row.actual_arrival_at, locale, tenantTz)}
        </span>
      );
    case "onTime":
      return <OnTimeIcon value={row.is_on_time} />;
    case "rating":
      return row.rating != null ? (
        <span
          className="inline-flex items-center gap-1.5"
          title={row.feedback_comment ?? undefined}
        >
          <RatingStars value={row.rating} />
          <span className="text-xs font-medium text-gray-700">
            {row.rating}
          </span>
          {row.feedback_comment ? (
            <span className="max-w-[160px] truncate text-xs text-gray-400">
              {row.feedback_comment}
            </span>
          ) : null}
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    default:
      return null;
  }
}

export function OrdersReportTable({ params, visibleKeys }: Props) {
  const { t, i18n } = useTranslation();
  // TASK-5043: arrival instants render in the tenant's zone so they are
  // comparable with the tenant-wall-clock time window in the next column.
  const tenantTz = useTenantTimezone();
  const [page, setPage] = useState(1);

  const COLUMNS = [
    { key: "orderName", label: t("reports.ordersTable.orderName") },
    { key: "customer", label: t("reports.ordersTable.customer") },
    { key: "address", label: t("reports.ordersTable.address") },
    { key: "route", label: t("reports.ordersTable.route") },
    { key: "vehicle", label: t("reports.ordersTable.vehicle") },
    { key: "status", label: t("reports.ordersTable.status") },
    { key: "timeWindow", label: t("reports.ordersTable.timeWindow") },
    { key: "arrivedAt", label: t("reports.ordersTable.arrivedAt") },
    { key: "onTime", label: t("reports.ordersTable.onTime") },
    { key: "rating", label: t("reports.ordersTable.rating") },
  ];

  const visibleCols = COLUMNS.filter((col) => visibleKeys.includes(col.key));

  useEffect(() => {
    setPage(1);
  }, [params]);

  const { data, isLoading } = useQuery({
    queryKey: ["reports", "orders", params, page],
    queryFn: () => getOrdersReport({ ...params, page }),
  });

  const rows = data?.results ?? [];
  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/60">
            {visibleCols.map((col) => (
              <th
                key={col.key}
                className="whitespace-nowrap px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <tr key={i} className="border-b border-gray-50 last:border-0">
                  {Array.from({ length: visibleCols.length }).map((_, j) => (
                    <td key={j} className="px-3 py-3">
                      <Skeleton className="h-4 w-20" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => (
                <tr
                  key={row.order_id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50"
                >
                  {visibleCols.map((col) => (
                    <td
                      key={col.key}
                      className="whitespace-nowrap px-3 py-3 text-xs"
                    >
                      {renderOrderCell(
                        col.key,
                        row,
                        t,
                        i18n.language,
                        tenantTz,
                      )}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>

      {!isLoading && rows.length === 0 && (
        <EmptyState icon={Package} title={t("reports.ordersTable.noData")} />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
          <span className="text-xs text-gray-400">
            {t("reports.pagination.showing", {
              from: (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, data?.count ?? 0),
              total: data?.count ?? 0,
            })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t("common.previous")}
            </button>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t("common.next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
